/**
 * Invites on the writer's device (decisions 75, 63; principle 10; part 38b log, principles DI1–DI10, tables IW, IR and
 * PU): a book's share made (its record first, then the relay's by a paid session), each invite's secrets made and stored
 * before the relay hears of it, sent by the owner proof; its settings changed, its removal and a new invite after a
 * reset; the relay's list read into the record (DI8); and the reconcile, which sends what the relay should hold of each
 * invite and pushes each item of the copy that changed, sealed off the page, in the writer's own session (IW14). One
 * book's work runs one call at a time. Every effect is injected: the store, the relay (session and public), WebCrypto,
 * the sealing, the randomness and the clock, so it runs against the relay in tests.
 */
import { encodeInviteCopy, toHex } from '@cockatrice/core';
import type { CreateResult, InviteNotes, InviteRange, InviteRecord, InviteRole, ShareEnd, ShareRecord } from '@cockatrice/storage';
import type { RelayClient, RelayError, Reply } from '../account/client.ts';
import { adoptListed, earlierEnd, holdersOf, inviteOpen, toBase64Id, itemHash, latestPushed, MAX_COPY_PLAIN, needsPush, planItems, pruned, settingsOf, versionAt } from './copies.ts';
import type { CopyBook, PlannedItem } from './copies.ts';
import { inviteSecretsOf, inviteUrl, newInviteSecrets, newShareSecrets, shareSecretsOf } from './invite-secrets.ts';
import type { ShareSecrets } from './invite-secrets.ts';
import { changeBody, copyFrame, inviteBody, isOpen, readEndedAll, readOk, readOwnerList, readShareList, settingsKey, shareBody } from './invite-wire.ts';
import type { InviteState, ListedShare, OwnerList } from './invite-wire.ts';
import type { SessionRelay } from './links.ts';
import type { SealedCopy, SealJob } from './seal.ts';
import { placesFor } from './note-plan.ts';
import { ShareNotes } from './share-notes.ts';
import type { ShareNoteStore } from './share-notes.ts';
import { bytesOfHex } from './secrets.ts';
import type { Random } from './secrets.ts';

/** What the invites keep in the local store, through the workspace (its lock); the notes across a share's too (part 38e). */
export interface InviteStore extends ShareNoteStore {
  /** Every share record this device holds (the readable ones). */
  shares(): Promise<readonly ShareRecord[]>;
  /** Stores a new share's record under its ID only if no record holds it (DI3), with no lock. */
  create(record: ShareRecord): Promise<CreateResult>;
  /** Deletes `record` (a share the relay refused to make, with no invite), under the lock; false when it wasn't. */
  remove(record: ShareRecord): Promise<boolean>;
  /** Book `bookId` as a copy is made of it (table N; RC11: open or closed), when it is in the list and reads whole; null otherwise. */
  book(bookId: string): Promise<CopyBook | null>;
}

/** What the invites run on. */
export interface InviteDeps {
  readonly session: SessionRelay;
  /** The relay without a session (the owner's routes, IP1), or null with accounts off. */
  readonly relay: () => RelayClient | null;
  readonly store: InviteStore;
  readonly subtle: SubtleCrypto;
  readonly random: Random;
  readonly now: () => number;
  /** The app's own origin, where invite links point (part 38c opens them). */
  readonly appOrigin: string;
  /** DI6: seals an item off the page; null when it couldn't. */
  readonly seal: (job: SealJob) => Promise<SealedCopy | null>;
  /** Part 38e, table DA: this device's public key, naming it to a share's notes; null for none (nothing is pulled). */
  readonly device: () => Promise<Uint8Array<ArrayBuffer> | null>;
  /**
   * Fix-next 84, table BO: the account signed in now, whose own list says which shares are its own; absent, one account
   * for the instance's life (the tests, the bench).
   */
  readonly account?: () => string | null;
}

/** The settings the writer gives a person (decision 75). */
export interface InviteChoice {
  readonly role: InviteRole;
  readonly notes: InviteNotes;
  readonly export: boolean;
  readonly endsAt: number | null;
  /** The chapters given, or null for every chapter. */
  /** The range given, or null for every chapter (table CS). */
  readonly chapters: InviteRange | null;
}

/** What inviting did. */
export type InviteOutcome =
  | { readonly kind: 'invited'; readonly invite: InviteRecord; readonly url: string }
  /** DI3, IR's U: stored, not yet at the relay (the network): sent at the next reconcile; its link works once it is. */
  | { readonly kind: 'unsent'; readonly invite: InviteRecord; readonly url: string }
  /** The relay said no (decision 63's purchase, its caps): nothing kept. */
  | { readonly kind: 'refused'; readonly error: RelayError }
  | { readonly kind: 'failed' };

/** What a change or a removal did. */
export type ChangeOutcome = { readonly kind: 'done' } | { readonly kind: 'refused'; readonly error: RelayError } | { readonly kind: 'failed' };

/**
 * Fix-next 84, BO6: how long a read of the account's list stands for a share it didn't name (made since on another of the
 * account's devices, it is pushed from here once the minute is out).
 */
export const OWNED_LIST_PAUSE_MS = 60_000;

/** Why a reconcile stopped short (table PU; part 38e's WN9, WN11), said on the Sharing page. */
export type PushProblem =
  | 'over-quota'
  | 'share-full'
  | 'too-large'
  | 'failed'
  | 'elsewhere'
  | 'settings'
  | 'note-too-large'
  | 'notes-full'
  /** Fix-next 67, table UR: the relay holds a later version of an item (IN17: another device's, or a clock ahead of this one). */
  | 'stale'
  /** UR4–UR6: an unsent invite's making refused: no purchase, the share's 50 people, the relay's daily count. */
  | 'invite-unpaid'
  | 'invite-full'
  | 'invite-later';

/** What a reconcile did: the items it pushed and what, if anything, stopped one. */
export interface Reconciled {
  readonly pushed: number;
  readonly problem: PushProblem | null;
  /** Fix-next 67, UR2: an unsent invite's making didn't reach the relay (the network, a server error): tried again soon. */
  readonly unsent?: true;
}

/** UR3–UR7: what an unsent invite's refused making says; a change's refusal (S′) is always `settings` (CS‴). */
function unsentProblem(error: RelayError): PushProblem {
  if (error === 'purchase-required') return 'invite-unpaid';
  if (error === 'share-full') return 'invite-full';
  return error === 'rate-limited' ? 'invite-later' : 'settings';
}

/** The new-invite request's own fields besides the choice. */
export interface InviteRequest extends InviteChoice {
  readonly bookId: string;
  /** The address, in canonical form (`canonicalEmail`). */
  readonly email: string;
}

const FAILED = Object.freeze({ kind: 'failed' as const });
const DONE = Object.freeze({ kind: 'done' as const });

/** The refusals that mean the share is over at the relay (DI8). */
function endOf(error: RelayError): ShareEnd | null {
  if (error === 'share-ended') return 'ended';
  return error === 'share-gone' ? 'gone' : null;
}

/** `record` with invite `id` replaced by `invite`, or taken out when `invite` is null. */
function withInvite(record: ShareRecord, id: string, invite: InviteRecord | null): ShareRecord {
  return { ...record, invites: invite === null ? record.invites.filter((each) => each.id !== id) : record.invites.map((each) => (each.id === id ? invite : each)) };
}

/** The invites. */
export class Invites {
  readonly #deps: InviteDeps;
  /** One book's calls in turn: each waits for the one before (table PU: two reconciles never run at once on a record). */
  readonly #turns = new Map<string, Promise<unknown>>();
  /** The relay's last list of each share, by record ID (DI8). */
  readonly #lists = new Map<string, OwnerList>();
  /** RC10: each book's reconcile running, and the one asked meanwhile, waiting for it. */
  readonly #running = new Map<string, Promise<Reconciled>>();
  /** Fix-next 84, table BO: by account, the wire IDs of the shares its own list named. */
  readonly #owned = new Map<string, Set<string>>();
  /** BO6: by account, when its list was last read. */
  readonly #listedAt = new Map<string, number>();
  readonly #queued = new Map<string, Promise<Reconciled>>();
  /** Part 38e: the notes across each share, run in the reconcile. */
  readonly #notes: ShareNotes;

  /** Deep-fix r6: each chapter's copy plaintext while its title and text are the same, so an unchanged one isn't encoded again. */
  readonly #plains = new Map<string, { readonly title: string; readonly text: string; readonly plain: Uint8Array<ArrayBuffer> }>();
  /** Deep-fix r6: each plaintext's hash, so an unchanged chapter's isn't taken again. */
  readonly #hashes = new WeakMap<Uint8Array, string>();

  constructor(deps: InviteDeps) {
    this.#deps = deps;
    this.#notes = new ShareNotes({
      relay: deps.relay,
      store: deps.store,
      subtle: deps.subtle,
      random: deps.random,
      now: deps.now,
      device: deps.device,
      turn: (bookId, body) => this.#turn(bookId, body),
      end: (record, why) => this.#end(record, why),
      refresh: (record, secrets) => this.#refresh(record, secrets),
    });
  }

  /** Deep-fix r6: `chapter`'s copy plaintext, the one kept while its title and text are the same. */
  #chapterPlain(chapter: CopyBook['chapters'][number]): Uint8Array<ArrayBuffer> {
    const kept = this.#plains.get(chapter.id);
    if (kept?.title === chapter.title && kept.text === chapter.text) return kept.plain;
    const plain = encodeInviteCopy({ kind: 'chapter', title: chapter.title, text: chapter.text });
    this.#plains.set(chapter.id, { title: chapter.title, text: chapter.text, plain });
    return plain;
  }

  /** Runs `body` after book `bookId`'s calls before it. */
  #turn<T>(bookId: string, body: () => Promise<T>): Promise<T> {
    const before = this.#turns.get(bookId) ?? Promise.resolve();
    const run = before.then(body, body);
    this.#turns.set(
      bookId,
      run.catch(() => undefined),
    );
    return run;
  }

  /** The relay's last list of share record `id`, if read. */
  listOf(id: string): OwnerList | undefined {
    return this.#lists.get(id);
  }

  /** The relay's last state of invite `invite` of share record `share`, if listed. */
  stateOf(share: string, invite: string): InviteState | undefined {
    return this.#lists.get(share)?.invites.find((listed) => listed.id === toBase64Id(invite))?.state;
  }

  /** The relay's states of `record`'s invites, by the record's invite IDs (those it listed). */
  #states(record: ShareRecord): ReadonlyMap<string, InviteState> {
    const states = new Map<string, InviteState>();
    for (const invite of record.invites) {
      const state = this.stateOf(record.id, invite.id);
      if (state !== undefined) states.set(invite.id, state);
    }
    return states;
  }

  /** IW1: the invite link of `invite` (Resend: the same link; no relay call). */
  linkOf(invite: Pick<InviteRecord, 'claim' | 'key'>): string {
    return inviteUrl(this.#deps.appOrigin, { claim: bytesOfHex(invite.claim), key: bytesOfHex(invite.key) });
  }

  /**
   * Deep-fix r3, BU′: every open share record of book `bookId` with its secrets, newest first: those held whose ID is
   * their secret's (table BU). A book can hold more than one (an update's staged share beside one made here).
   */
  async #opens(bookId: string): Promise<readonly { readonly record: ShareRecord; readonly secrets: ShareSecrets; readonly owned: boolean | null }[]> {
    const opens: { readonly record: ShareRecord; readonly secrets: ShareSecrets; readonly owned: boolean | null }[] = [];
    for (const record of await this.#deps.store.shares()) {
      if (record.bookId !== bookId || record.ended !== null) continue;
      const secrets = await shareSecretsOf(this.#deps.subtle, record);
      // Table BU, deep-fix r6: a record holding an invite whose ID isn't its claim's own is forged whole: never used.
      if (secrets !== null && (await this.#invitesTrue(record))) opens.push({ record, secrets, owned: await this.#ownedShare(record, secrets) });
    }
    return opens.sort((a, b) => b.record.createdAt - a.record.createdAt);
  }

  /** BO2: share `share` (its wire ID) kept as the signed-in account's own. */
  #ownedBy(share: string): void {
    const account = this.#accountKey();
    if (account === null) return;
    const owned = this.#owned.get(account) ?? new Set<string>();
    owned.add(share);
    this.#owned.set(account, owned);
  }

  /** Table BO: the account the ownership below is asked for ('' when the deps name none). */
  #accountKey(): string | null {
    return this.#deps.account === undefined ? '' : this.#deps.account();
  }

  /**
   * Fix-next 84, table BO (the security audit of 2026-11-02): whether share record `record` is the signed-in account's own,
   * so its owner proof may write to the relay (invites, their settings, notes, role keys). A record a bundle brought
   * proves only itself (table BU), never whose share it is: another account's, restored or updated in, would take the
   * writer's notes. BO1: never made at the relay, it is this device's own (making it is refused for another's ID). BO2: made,
   * it is the account's once the account's own list (`GET /v1/shares`, by session) names it; a share never changes
   * account, so a name is kept for the account's sake. BO3: not named, another account's: no write (IW14 keeps the record).
   * BO4: the list unread (offline, signed out): not known, `null`.
   */
  async #ownedShare(record: ShareRecord, secrets: ShareSecrets): Promise<boolean | null> {
    if (!record.made) return true;
    const account = this.#accountKey();
    if (account === null) return null;
    if (this.#owned.get(account)?.has(secrets.share) === true) return true;
    // BO6: not named by a list read in the last minute: another account's still (one run asks once, not per share).
    const read = this.#listedAt.get(account);
    if (read !== undefined && this.#deps.now() - read < OWNED_LIST_PAUSE_MS) return false;
    if ((await this.accountShares()) === null) return null;
    return this.#owned.get(account)?.has(secrets.share) === true;
  }

  /** Table BU: whether every invite of `record` has the ID its claim secret derives. */
  async #invitesTrue(record: ShareRecord): Promise<boolean> {
    for (const invite of record.invites) if ((await inviteSecretsOf(this.#deps.subtle, invite)) === null) return false;
    return true;
  }

  /**
   * BU′: the share a new invite of book `bookId` joins: its newest open one, or null. BO3: never another account's; BO4:
   * one not known yet may hold it (stored, sent only once the reconcile knows it the account's).
   */
  async #open(bookId: string): Promise<{ readonly record: ShareRecord; readonly secrets: ShareSecrets } | null> {
    return (await this.#opens(bookId)).find((open) => open.owned !== false) ?? null;
  }

  /** BU′: the open share of book `bookId` holding invite `id`, or null; BO2: only the account's own. */
  async #holding(bookId: string, id: string): Promise<{ readonly record: ShareRecord; readonly secrets: ShareSecrets } | null> {
    return (await this.#opens(bookId)).find((open) => open.owned === true && open.record.invites.some((invite) => invite.id === id)) ?? null;
  }

  /** `record` as stored now (another call of this tab may have written it), or `record` itself when it can't be read. */
  async #current(record: ShareRecord): Promise<ShareRecord> {
    return (await this.#deps.store.share(record.id)) ?? record;
  }

  /** DI8: `record` ended `why` at the relay's word; once (`gone` is the last word on any end). */
  async #end(record: ShareRecord, why: ShareEnd): Promise<void> {
    const current = await this.#current(record);
    if (current.ended === null) await this.#deps.store.put({ ...current, ended: { at: this.#deps.now(), why } });
    // A record ended `gone` is never read against the relay again (`#refreshAll`), so only an `ended` one gets here.
    else if (why === 'gone') await this.#deps.store.put({ ...current, ended: { at: current.ended.at, why } });
  }

  /**
   * DI3, DI10: the book's open share, made for it first when none is held: its record stored, then the relay's by the
   * paid session. A refusal deletes a record with no invite (the relay holds nothing); a network failure keeps it.
   */
  async #made(bookId: string): Promise<{ readonly record: ShareRecord; readonly secrets: ShareSecrets } | { readonly kind: 'refused'; readonly error: RelayError } | null> {
    const { store, subtle, random, now, session } = this.#deps;
    let open = await this.#open(bookId);
    if (open === null) {
      const secrets = await newShareSecrets(subtle, random);
      const record: ShareRecord = { id: toHex(secrets.id), bookId, secret: toHex(secrets.secret), createdAt: now(), made: false, invites: [], pushed: [], roleKeys: [], ended: null };
      if ((await store.create(record)).kind !== 'created') return null;
      open = { record, secrets };
    }
    if (open.record.made) return open;
    const made = await session.post('/v1/shares', shareBody(open.secrets.share, open.secrets.proof), readOk);
    if (made.kind === 'refused') {
      if (open.record.invites.length === 0) await store.remove(open.record);
      return { kind: 'refused', error: made.error };
    }
    if (made.kind === 'failed') return null;
    // BO2: made at the relay by this account's session: its own, with no list to read.
    this.#ownedBy(open.secrets.share);
    const record = { ...(await this.#current(open.record)), made: true };
    return (await store.put(record)) ? { record, secrets: open.secrets } : null;
  }

  /**
   * IW1, DI3, IR (Send): invites `request.email` to book `request.bookId`: the share made if new, the invite's secrets made
   * and stored, then sent by the owner proof. A refusal takes the invite out of the record again; a network failure leaves
   * it unsent (the next reconcile sends it).
   */
  invite(request: InviteRequest): Promise<InviteOutcome> {
    return this.#turn(request.bookId, async () => {
      const { store, subtle, random, now } = this.#deps;
      const book = await store.book(request.bookId);
      if (book === null) return FAILED;
      const made = await this.#made(request.bookId);
      if (made === null) return FAILED;
      if ('kind' in made) return made;
      const secrets = await newInviteSecrets(subtle, random);
      const at = now();
      const invite: InviteRecord = {
        id: toHex(secrets.id),
        email: request.email,
        role: request.role,
        notes: request.notes,
        export: request.export,
        endsAt: request.endsAt,
        chapters: request.chapters,
        claim: toHex(secrets.claim),
        key: toHex(secrets.key),
        createdAt: at,
        changedAt: at,
        sent: null,
        removedAt: null,
      };
      const record = { ...made.record, invites: [...made.record.invites, invite] };
      if (!(await store.put(record))) return FAILED;
      const sent = await this.#send(record, made.secrets, invite, book);
      const url = this.linkOf(invite);
      if (sent.kind === 'refused') {
        await store.put(withInvite(await this.#current(record), invite.id, null));
        return sent;
      }
      return sent.kind === 'done' ? { kind: 'invited', invite: { ...invite, sent: settingsKey(settingsOf(invite, book)) }, url } : { kind: 'unsent', invite, url };
    });
  }

  /**
   * Table IR (Send, Change): what the relay should hold of `invite` for `book`, sent: its making while unsent (a retry is
   * the same invite: `done`), else its change; `invite-exists` (unsent, changed meanwhile) becomes a change. The record
   * marks what the relay took; a refusal ending the share ends the record.
   */
  async #send(record: ShareRecord, secrets: ShareSecrets, invite: InviteRecord, book: CopyBook): Promise<ChangeOutcome> {
    const relay = this.#deps.relay();
    const keys = await inviteSecretsOf(this.#deps.subtle, invite);
    if (relay === null || keys === null) return FAILED;
    const settings = settingsOf(invite, book);
    let reply: Reply<true>;
    if (invite.sent === null) {
      reply = await relay.post('/v1/shares/invite', inviteBody(secrets.share, secrets.proof, keys.invite, keys.proof, invite.email, settings), readOk);
      if (reply.kind === 'refused' && reply.error === 'invite-exists') reply = await relay.post('/v1/shares/invite/change', changeBody(secrets.share, secrets.proof, keys.invite, settings), readOk);
    } else {
      reply = await relay.post('/v1/shares/invite/change', changeBody(secrets.share, secrets.proof, keys.invite, settings), readOk);
    }
    if (reply.kind === 'failed') return FAILED;
    if (reply.kind === 'refused') {
      const end = endOf(reply.error);
      if (end !== null) await this.#end(record, end);
      return { kind: 'refused', error: reply.error };
    }
    const current = await this.#current(record);
    const held = current.invites.find((each) => each.id === invite.id);
    // Marked only while the record still holds the settings sent (a change made meanwhile is sent at its own turn).
    // Deep-fix r2, table PH: the items it no longer holds lose it as a holder (the relay dropped those wraps).
    if (held?.changedAt === invite.changedAt) {
      const marked = withInvite(current, invite.id, { ...held, sent: settingsKey(settings) });
      await this.#deps.store.put({ ...marked, pushed: pruned(marked.pushed, invite.id, new Set(settings.chapters)) });
    }
    return DONE;
  }

  /** IW11, IR (Change): invite `id` of book `bookId` given `choice`: the record first, then the relay's change. */
  change(bookId: string, id: string, choice: InviteChoice): Promise<ChangeOutcome> {
    return this.#turn(bookId, async () => {
      const open = await this.#holding(bookId, id);
      const invite = open?.record.invites.find((each) => each.id === id);
      const book = await this.#deps.store.book(bookId);
      if (open === null || invite?.removedAt !== null || book === null) return FAILED;
      const { role, notes, export: exported, endsAt, chapters } = choice;
      const changed: InviteRecord = { ...invite, role, notes, export: exported, endsAt, chapters, changedAt: Math.max(this.#deps.now(), invite.changedAt + 1) };
      const record = withInvite(open.record, id, changed);
      if (!(await this.#deps.store.put(record))) return FAILED;
      const sent = await this.#send(record, open.secrets, changed, book);
      if (sent.kind === 'refused' && sent.error === 'invite-ended') await this.#refresh(record, open.secrets);
      return sent;
    });
  }

  /**
   * IW5, IR (Remove), DI8: invite `id` of book `bookId` removed by the owner proof; marked removed only on the relay's
   * word. One the relay never held (unsent) is taken out of the record.
   */
  remove(bookId: string, id: string): Promise<ChangeOutcome> {
    return this.#turn(bookId, () => this.#remove(bookId, id));
  }

  async #remove(bookId: string, id: string): Promise<ChangeOutcome> {
    const relay = this.#deps.relay();
    const open = await this.#holding(bookId, id);
    const invite = open?.record.invites.find((each) => each.id === id);
    const keys = invite === undefined ? null : await inviteSecretsOf(this.#deps.subtle, invite);
    if (relay === null || open === null || invite === undefined || keys === null) return FAILED;
    if (invite.removedAt !== null) return DONE;
    const removed = await relay.post('/v1/shares/invite/remove', shareBody(open.secrets.share, open.secrets.proof, { invite: keys.invite }), readOk);
    if (removed.kind === 'failed') return FAILED;
    const current = await this.#current(open.record);
    if (removed.kind === 'refused' && removed.error !== 'invite-gone') {
      const end = endOf(removed.error);
      if (end !== null) await this.#end(current, end);
      return end === null ? { kind: 'refused', error: removed.error } : DONE;
    }
    // IR: an invite the relay never held goes from the record; one it took, or holds no more, is marked removed.
    const gone = removed.kind === 'refused' && invite.sent === null;
    const after = withInvite(current, id, gone ? null : { ...invite, removedAt: this.#deps.now() });
    return (await this.#deps.store.put(after)) ? DONE : FAILED;
  }

  /**
   * IW2, DI9: a new invite for invite `id`'s person after their reset (`lost`): the old one removed (or found over), then a
   * fresh invite at the same address with the same settings, its own secrets.
   */
  async renew(bookId: string, id: string): Promise<InviteOutcome> {
    const open = await this.#holding(bookId, id);
    const invite = open?.record.invites.find((each) => each.id === id);
    if (open === null || invite === undefined) return FAILED;
    const removed = await this.remove(bookId, id);
    if (removed.kind !== 'done') return removed;
    return this.invite({ bookId, email: invite.email, role: invite.role, notes: invite.notes, export: invite.export, endsAt: invite.endsAt, chapters: invite.chapters });
  }

  /** DI8: the relay's list of `record` read in: the share's end, and each invite the relay says removed or ended with it. */
  async #refresh(record: ShareRecord, secrets: ShareSecrets): Promise<OwnerList | null> {
    const relay = this.#deps.relay();
    if (relay === null) return null;
    const listed = await relay.post('/v1/shares/invites', shareBody(secrets.share, secrets.proof), readOwnerList);
    if (listed.kind === 'refused' && listed.error === 'share-gone') {
      await this.#end(record, 'gone');
      return null;
    }
    if (listed.kind !== 'ok') return null;
    this.#lists.set(record.id, listed.value);
    const current = await this.#current(record);
    const byId = new Map(listed.value.invites.map((invite) => [invite.id, invite]));
    let pushed = current.pushed;
    const marked = current.invites.map((invite) => {
      const known = byId.get(toBase64Id(invite.id));
      if (invite.removedAt !== null) return invite;
      // Deep-fix r6, TD″: one sent that the relay lists no more was swept (90 days after its end): over for good.
      if (known === undefined) return invite.sent === null ? invite : { ...invite, removedAt: this.#deps.now() };
      if (known.state === 'removed') return { ...invite, removedAt: this.#deps.now() };
      // Table CS, PH: the items the relay no longer gives it (any device's change) lose it as a holder.
      pushed = pruned(pushed, invite.id, new Set(known.chapters));
      // Deep-fix r10, CS‴: with no change of this device's waiting, the listed role, notes, export and end.
      return adoptListed(invite, known);
    });
    const prunedAny = pushed.length !== current.pushed.length || pushed.some((entry, at) => entry !== current.pushed[at]);
    if (prunedAny || marked.some((invite, at) => invite !== current.invites[at])) await this.#deps.store.put({ ...current, invites: marked, pushed });
    if (listed.value.endedAt !== null) await this.#end(record, 'ended');
    return listed.value;
  }

  /** DI8: every record of book `bookId` (or every book, with none) read against the relay's lists. */
  refresh(bookId: string | null = null): Promise<void> {
    return this.#refreshAll(bookId);
  }

  async #refreshAll(bookId: string | null): Promise<void> {
    for (const record of await this.#deps.store.shares()) {
      if ((bookId !== null && record.bookId !== bookId) || record.ended?.why === 'gone' || !record.made) continue;
      const secrets = await shareSecretsOf(this.#deps.subtle, record);
      if (secrets !== null) await this.#turn(record.bookId, async () => {
        await this.#refresh(record, secrets);
      });
    }
  }

  /**
   * Table PU, IR, DI5–DI7, IW14, table RC: book `bookId`'s open share brought to the relay: each open invite's settings
   * sent where the relay's differ (or it never had them), then each item of the copy whose plaintext or holders changed
   * pushed at a new version, sealed off the page, in the writer's session. RC10: never two at once for a book: one asked
   * while one runs runs once after it (asked again meanwhile, the same run). RC8: the book's turn is held only around a
   * record's read and write, so the writer's actions run between items.
   */
  reconcile(bookId: string): Promise<Reconciled> {
    const queued = this.#queued.get(bookId);
    if (queued !== undefined) return queued;
    const running = this.#running.get(bookId);
    if (running === undefined) return this.#start(bookId);
    const next = running.then(
      () => this.#next(bookId),
      () => this.#next(bookId),
    );
    this.#queued.set(bookId, next);
    return next;
  }

  /** RC10: the queued run of book `bookId` starting: no longer queued. */
  #next(bookId: string): Promise<Reconciled> {
    this.#queued.delete(bookId);
    return this.#start(bookId);
  }

  /** RC10: a run of book `bookId`'s reconcile, held as the one running until it ends. */
  #start(bookId: string): Promise<Reconciled> {
    const run = this.#reconcile(bookId).finally(() => {
      this.#running.delete(bookId);
    });
    this.#running.set(bookId, run);
    return run;
  }

  /** RC: the settings sent (in the book's turn), then each item in turn of its own, its sealing and upload outside it. */
  async #reconcile(bookId: string): Promise<Reconciled> {
    // BU′: every open share of the book in turn; the first problem met is the one said.
    let pushed = 0;
    let problem: PushProblem | null = null;
    let unsent = false;
    for (const open of await this.#turn(bookId, () => this.#opens(bookId))) {
      const done = await this.#reconcileShare(bookId, open.record.id);
      pushed += done.pushed;
      problem ??= done.problem;
      unsent ||= done.unsent === true;
    }
    await this.#pullEnded(bookId);
    return unsent ? { pushed, problem, unsent } : { pushed, problem };
  }

  /**
   * Part 38e, deep-fix r2 (table EP): each share of book `bookId` the relay ended (not gone), its notes pulled alone, so an
   * invitee's last notes reach the writer; one the relay holds no more is ended `gone` by the pull and never asked again.
   */
  async #pullEnded(bookId: string): Promise<void> {
    const ended = (await this.#deps.store.shares()).filter((record) => record.bookId === bookId && record.made && record.ended?.why === 'ended');
    if (ended.length === 0) return;
    const book = await this.#deps.store.book(bookId);
    if (book === null) return;
    for (const record of ended) {
      const secrets = await shareSecretsOf(this.#deps.subtle, record);
      if (secrets !== null && (await this.#invitesTrue(record))) await this.#notes.pull(bookId, record.id, secrets, book);
    }
  }

  /** RC, for share `id` of book `bookId`. */
  async #reconcileShare(bookId: string, id: string): Promise<Reconciled> {
    const prepared = await this.#turn(bookId, () => this.#prepare(bookId, id));
    if ('pushed' in prepared) return prepared;
    const { secrets, book } = prepared;
    let pushed = 0;
    // Part 38e, DN10: the notes first (they are small, and a note waits on no chapter).
    const noted = await this.#notes.run(bookId, prepared.record.id, secrets, prepared.list, book);
    let problem: PushProblem | null = prepared.problem ?? noted.problem;
    for (const planned of prepared.items) {
      const job = await this.#turn(bookId, () => this.#job(prepared.record.id, book, prepared.list, planned));
      if (job === 'ended') return { pushed, problem };
      if (job === null) continue;
      // RC5: an item too large is left; the rest go.
      if (job.item.plain.length > MAX_COPY_PLAIN) {
        problem = 'too-large';
        continue;
      }
      const outcome = await this.#push(bookId, job, secrets);
      if (outcome === 'stored') pushed += 1;
      else if (outcome === 'ended') return { pushed, problem };
      else if (outcome === 'skipped') problem = 'failed';
      // Fix-next 67, UR8: a later version held is said (IN17's next reconcile still tries it again).
      else if (outcome === 'stale') problem ??= 'stale';
      else return { pushed, problem: outcome };
    }
    return { pushed, problem };
  }

  /** RC: the open share, its book and the relay's list read, each invite's settings sent; the items to look at, by name. */
  async #prepare(bookId: string, id: string): Promise<Reconciled | { readonly record: ShareRecord; readonly secrets: ShareSecrets; readonly book: CopyBook; readonly list: OwnerList; readonly items: readonly { readonly item: PlannedItem; readonly hash: string }[]; readonly problem: PushProblem | null }> {
    const { store, now } = this.#deps;
    const open = (await this.#opens(bookId)).find((each) => each.record.id === id);
    if (open?.record.made !== true) return { pushed: 0, problem: null };
    // BO3: another account's share takes nothing of the writer's (no settings, notes, role keys or copies); BO4: not
    // known yet, tried again at the next run.
    if (open.owned === false) return { pushed: 0, problem: null };
    if (open.owned === null) return { pushed: 0, problem: 'failed' };
    // RC11: the book as stored when it isn't the open one.
    const book = await store.book(bookId);
    if (book === null) return { pushed: 0, problem: 'failed' };
    // Deep-fix r5, TD6, TD7: the relay's list read fresh every run (another device may have invited since); unread, no
    // copy goes (below), since TD2 can't be judged without it.
    const first = await this.#refresh(open.record, open.secrets);
    let sentAny = false;
    let refused: PushProblem | null = null;
    const record = await this.#current(open.record);
    if (record.ended !== null) return { pushed: 0, problem: null };
    const states = this.#states(record);
    for (const invite of record.invites) {
      // IW5, RC3: an invite over (here, in the relay's word, or by the device's clock) needs nothing sent; CS′: a listed
      // one by its listed end.
      const known = first?.invites.find((each) => each.id === toBase64Id(invite.id));
      // Fix-next 85, CS⁗: never past this device's own end either (the list ends sooner, never later).
      if (!inviteOpen(known === undefined ? invite : { ...invite, endsAt: earlierEnd(invite.endsAt, known.endsAt) }, states.get(invite.id), now())) continue;
      if (invite.sent === settingsKey(settingsOf(invite, book))) continue;
      const sent = await this.#send(record, open.secrets, invite, book);
      // Fix-next 67, UR2: an unsent invite's making not reaching the relay is tried again soon (`unsent`), not at the next save.
      if (sent.kind === 'failed') return invite.sent === null ? { pushed: 0, problem: 'failed', unsent: true } : { pushed: 0, problem: 'failed' };
      sentAny ||= sent.kind === 'done';
      // RC1, RC2: a refusal keeps that invite as the relay holds it; the list read again, the others go on. CS‴: said.
      // Fix-next 67, UR3–UR7: an unsent invite's refusal in words that can help (a lapsed trial's is the purchase).
      if (sent.kind === 'refused') {
        refused ??= invite.sent === null ? unsentProblem(sent.error) : 'settings';
        await this.#refresh(record, open.secrets);
      }
    }
    const after = await this.#current(record);
    if (after.ended !== null) return { pushed: 0, problem: null };
    // Table CS: what was sent this run is in the relay's list only when read again (one more request, then).
    const list = sentAny && first !== null ? await this.#refresh(after, open.secrets) : first;
    if (list === null) return { pushed: 0, problem: 'failed' };
    // Deep-fix r4, TD2: an open invite the relay lists that this record lacks (made on another device): a new version
    // from here would cut them off, and this device holds no key to wrap for them. No copy goes; the writer is told.
    const held = new Set(after.invites.map((invite) => toBase64Id(invite.id)));
    if (list.invites.some((invite) => isOpen(invite.state) && !held.has(invite.id))) return { pushed: 0, problem: 'elsewhere' };
    // Deep-fix r3: planned and hashed once a run; each item's turn checks only its holders against the record then.
    const items: { readonly item: PlannedItem; readonly hash: string }[] = [];
    // Nothing changed (the common case, every save but the open chapter's): no item's turn at all.
    // Part 38e, table MP: each reader's manifest names the places of the notes it reads, found in the text copied now.
    const located = await this.#notes.located(bookId, after.id, book);
    for (const item of planItems(after, book, list, now(), (invite, chapters) => placesFor(after, invite, chapters, located), (chapter) => this.#chapterPlain(chapter))) {
      const hash = this.#hashes.get(item.plain) ?? (await itemHash(this.#deps.subtle, item));
      this.#hashes.set(item.plain, hash);
      if (needsPush(item, hash, after.pushed)) items.push({ item, hash });
    }
    return { record: after, secrets: open.secrets, book, list, items, problem: refused };
  }

  /**
   * RC8, RC14: `planned`'s holders taken again from share `id`'s record as it stands now (a removal since), with the
   * version past every one the record pushed; null when it needs no push (or no one holds it now), `ended` when the share
   * ended meanwhile.
   */
  async #job(id: string, book: CopyBook, list: OwnerList, planned: { readonly item: PlannedItem; readonly hash: string }): Promise<{ readonly item: PlannedItem; readonly hash: string; readonly time: number } | null | 'ended'> {
    const { store, now } = this.#deps;
    const record = await store.share(id);
    if (record?.ended !== null) return 'ended';
    // The list this run read (table CS: the one source of who is given what).
    const holders = holdersOf(record, book, list, now(), planned.item.item);
    if (holders.length === 0) return null;
    const item = { ...planned.item, holders };
    return needsPush(item, planned.hash, record.pushed) ? { item, hash: planned.hash, time: Math.max(now(), latestPushed(record.pushed) + 1) } : null;
  }

  /**
   * Table PU, RC4–RC9: one item sealed and pushed, outside the book's turn; the record marks it pushed when the relay took
   * it, in a turn of its own. `skipped`: this item refused (an invite closed meanwhile, the list read again), the rest go.
   */
  async #push(bookId: string, job: { readonly item: PlannedItem; readonly hash: string; readonly time: number }, secrets: ShareSecrets): Promise<'stored' | 'stale' | 'ended' | 'skipped' | PushProblem> {
    const { random, seal, session } = this.#deps;
    const { item, hash, time } = job;
    // The record's invites were checked against their claims as it was opened (`#opens`).
    const holders = item.holders.map((invite) => ({ invite: bytesOfHex(invite.id), key: bytesOfHex(invite.key), name: toBase64Id(invite.id) }));
    const version = versionAt(time, random);
    const sealed = await seal({ kind: 'seal', share: secrets.id, item: item.item, version, plain: item.plain, holders: holders.map(({ invite, key }) => ({ invite, key })) });
    if (sealed === null) return 'failed';
    // A sealing that wrapped for fewer or more holders than asked is no sealing (the worker's answer is read so too).
    if (sealed.wraps.length > holders.length) return 'failed';
    const wraps = new Map<string, Uint8Array>();
    for (const [at, holder] of holders.entries()) {
      const wrap = sealed.wraps[at];
      if (wrap === undefined) return 'failed';
      wraps.set(holder.name, wrap);
    }
    const answer = await session.postBytes('/v1/shares/copy', copyFrame(secrets.share, secrets.proof, item.item, version, wraps, sealed.bytes), readOk);
    if (answer.kind === 'failed') return 'failed';
    const id = toHex(secrets.id);
    if (answer.kind === 'refused') {
      // Deep-fix r2: the copy goes in the writer's own session, so `share-gone` here may mean another account's session
      // (IW14), not a purge: only the owner list's own word ends the record; else nothing is stored, tried later.
      if (answer.error === 'share-gone') {
        const ended = await this.#turn(bookId, async () => {
          const record = await this.#deps.store.share(id);
          if (record === undefined) return true;
          await this.#refresh(record, secrets);
          return (await this.#deps.store.share(id))?.ended !== null;
        });
        return ended ? 'ended' : 'failed';
      }
      if (answer.error === 'share-ended') {
        await this.#turn(bookId, async () => {
          const record = await this.#deps.store.share(id);
          if (record !== undefined) await this.#end(record, 'ended');
        });
        return 'ended';
      }
      if (answer.error === 'copy-stale') return 'stale';
      if (answer.error === 'over-quota' || answer.error === 'share-full') return answer.error;
      // RC4, RC9: an invite closed meanwhile (or removed while this was in flight): listed again, this item left.
      if (answer.error === 'bad-request') {
        await this.#turn(bookId, async () => {
          const record = await this.#deps.store.share(id);
          if (record !== undefined) await this.#refresh(record, secrets);
        });
        return 'skipped';
      }
      // IW14: another account's session, or signed out.
      return 'failed';
    }
    // RC14: this item's entry put onto the record as it stands now.
    await this.#turn(bookId, async () => {
      const current = await this.#deps.store.share(id);
      if (current === undefined) return;
      const entry = { item: item.item, version: toHex(version), hash, holders: item.holders.map((invite) => invite.id) };
      await this.#deps.store.put({ ...current, pushed: [...current.pushed.filter((each) => each.item !== item.item), entry] });
    });
    return 'stored';
  }

  /** IW6, IW8: every share of the account ended by session, then each record here read against the relay; how many ended. */
  async endAll(): Promise<number | null> {
    const ended = await this.#deps.session.post('/v1/shares/end-all', {}, readEndedAll);
    if (ended.kind !== 'ok') return null;
    await this.#refreshAll(null);
    return ended.value;
  }

  /** IW4: the account's shares as the relay lists them by session, or null; BO2: each one named kept as the account's. */
  async accountShares(): Promise<readonly ListedShare[] | null> {
    const account = this.#accountKey();
    const listed = await this.#deps.session.get('/v1/shares', readShareList);
    if (listed.kind !== 'ok') return null;
    // BO2: named for the account asked about; a list read across a change of account is kept for neither.
    if (account !== null && account === this.#accountKey()) {
      for (const share of listed.value) this.#ownedBy(share.id);
      this.#listedAt.set(account, this.#deps.now());
    }
    return listed.value;
  }
}

export { toBase64Id } from './copies.ts';
